// =============================================================================
// blockBoundary — shared GeoJSON helpers for farm block boundaries.
//
// A block boundary may be a single Polygon (one contiguous area) OR a
// MultiPolygon (2+ disjoint areas — e.g. a road splits a block into two
// pieces). Every reader (FarmMap / SprayMap / ScoutingMap / the editor) goes
// through these helpers so both shapes render, fit-to-bounds, and area-sum
// identically, and so legacy single-Polygon rows keep working untouched.
//
// Coordinates are [lng, lat] in degrees, GeoJSON-spec order (opposite of the
// usual lat/lng). Stored verbatim in blocks.boundary / boundary_override
// (JSONB) — see mig 281/282.
// =============================================================================

export type GeoJsonPolygon = {
  type:        'Polygon'
  coordinates: number[][][]      // [ring][point][lng, lat]
}

export type GeoJsonMultiPolygon = {
  type:        'MultiPolygon'
  coordinates: number[][][][]    // [polygon][ring][point][lng, lat]
}

export type BlockBoundary = GeoJsonPolygon | GeoJsonMultiPolygon

// Spherical-excess polygon area on WGS84. Treats the ring as a geodesic on a
// sphere of radius 6378137m — accurate to <0.1% for orchard-scale polygons.
// Coordinates are [lng, lat] in degrees. Returns square metres.
function sphericalPolygonAreaM2(ring: number[][]): number {
  if (!ring || ring.length < 4) return 0
  const R = 6378137
  let total = 0
  for (let i = 0; i < ring.length - 1; i++) {
    const [lng1, lat1] = ring[i]
    const [lng2, lat2] = ring[i + 1]
    const dLng = (lng2 - lng1) * Math.PI / 180
    total += dLng * (2 + Math.sin(lat1 * Math.PI / 180) + Math.sin(lat2 * Math.PI / 180))
  }
  return Math.abs(total * R * R / 2)
}

// Outer rings across every polygon. Polygon → the single outer ring;
// MultiPolygon → each polygon's outer ring. Interior rings (holes) are
// ignored — the UI doesn't draw them. Defensive against malformed JSON.
// fallow-ignore-next-line complexity
export function boundaryOuterRings(b: BlockBoundary | null | undefined): number[][][] {
  if (!b) return []
  if (b.type === 'MultiPolygon') {
    return (b.coordinates ?? [])
      .map(poly => poly?.[0])
      .filter((r): r is number[][] => Array.isArray(r))
  }
  const outer = b.coordinates?.[0]
  return Array.isArray(outer) ? [outer] : []
}

// Middle of the bounding box of every area in the given boundaries, or null
// when none has a drawable shape. The block editor opens here (zoom 16) when
// it is given a site's other blocks (harvest UF-06).
export function boundariesCentre(
  bs: Array<BlockBoundary | null | undefined>,
): { latitude: number; longitude: number } | null {
  const pts = bs.flatMap(b => boundaryOuterRings(b)).flat()
    .filter(c => Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1]))
  if (!pts.length) return null
  const lats = pts.map(c => c[1])
  const lngs = pts.map(c => c[0])
  return {
    latitude:  (Math.min(...lats) + Math.max(...lats)) / 2,
    longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
  }
}

// True when the boundary has at least one drawable area (a closed ring of
// ≥ 4 points). Replaces the old `coordinates?.[0]?.length >= 4` checks that
// silently dropped MultiPolygons.
export function boundaryHasShape(b: BlockBoundary | null | undefined): boolean {
  return boundaryOuterRings(b).some(ring => ring.length >= 4)
}

// Number of distinct drawn areas — drives the editor's "N areas" readout.
export function boundaryAreaCount(b: BlockBoundary | null | undefined): number {
  return boundaryOuterRings(b).filter(ring => ring.length >= 4).length
}

// Total hectares across all areas (sum of outer-ring areas). Holes are not
// subtracted (the UI can't draw them). Returns null when there is no area.
// fallow-ignore-next-line complexity
export function boundaryHectares(b: BlockBoundary | null | undefined): number | null {
  const rings = boundaryOuterRings(b)
  if (rings.length === 0) return null
  let m2 = 0
  for (const ring of rings) m2 += sphericalPolygonAreaM2(ring)
  if (!Number.isFinite(m2) || m2 <= 0) return null
  return +(m2 / 10000).toFixed(2)
}

// Back-compat single-polygon helper — veg section areas (BlockPlantings) stay
// single-Polygon, so this keeps the old call sites working.
export function polygonHectares(polygon: GeoJsonPolygon | null | undefined): number | null {
  return boundaryHectares(polygon ?? null)
}

// Even-odd ray-cast: is [lng, lat] inside the ring? Ring is a GeoJSON outer
// ring ([lng, lat] points, first == last). Planar test — exact enough at
// orchard scale, and GPS fixes carry metres of jitter anyway. A point exactly
// on an edge may land either side.
export function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

// True when the point lies inside any of the boundary's outer rings. Holes
// are ignored — consistent with the render/area helpers above.
export function pointInBoundary(lng: number, lat: number, b: BlockBoundary | null | undefined): boolean {
  return boundaryOuterRings(b).some(ring => ring.length >= 4 && pointInRing(lng, lat, ring))
}
